import {
  MessageAttributeValue,
  SendMessageCommand,
  SQSClient,
} from "@aws-sdk/client-sqs"
import { DbTransaction } from "@tc/middy-decorators"
import { eq, and, inArray } from "drizzle-orm"
import { partyAgency, partyUserRoleConstraint } from "@tc/drizzle-v2"

const sqs_client = new SQSClient({})
const queue_url = process.env["AGENCY_QUEUE_URL"]

export type Address = {
  physical_address?: string | null
  physical_city?: string | null
  physical_state?: string | null
  physical_country_code?: string | null
}

export type AgencyDoc = {
  id: string
  identifier: string
  agency_type: string
  display_name: string | null
  address?: Address
  source_system_id?: string | null
  accessible_by: string[]
}

type AgencyRow = {
  id: string
  identifier: string
  agency_type: string
  display_name: string | null
  physical_address: string | null
  physical_city: string | null
  physical_state: string | null
  physical_country_code: string | null
  source_system_id: string | null
}

/** Build AgencyDoc from raw row data */
const buildAgencyDoc = (row: AgencyRow, userAccess: string[]): AgencyDoc => ({
  id: row.id,
  identifier: row.identifier,
  agency_type: row.agency_type,
  display_name: row.display_name ?? "",
  address: {
    physical_address: row.physical_address ?? "",
    physical_city: row.physical_city ?? "",
    physical_state: row.physical_state ?? "",
    physical_country_code: row.physical_country_code ?? "",
  },
  source_system_id: row.source_system_id ?? "",
  accessible_by: userAccess.length > 0 ? userAccess : ["*"],
})

export const getAgencyData = async (
  tx: DbTransaction,
  id: string,
): Promise<AgencyDoc | undefined> => {
  const [agencyItem] = await tx
    .select({
      id: partyAgency.id,
      identifier: partyAgency.identifier,
      agency_type: partyAgency.agencyType,
      display_name: partyAgency.displayName,
      physical_address: partyAgency.physicalAddress,
      physical_city: partyAgency.city,
      physical_state: partyAgency.state,
      physical_country_code: partyAgency.countryCode,
      source_system_id: partyAgency.sourceSystemId,
    })
    .from(partyAgency)
    .where(and(eq(partyAgency.id, id), eq(partyAgency.active, true)))

  if (!agencyItem) {
    console.error(`Agency not found or inactive for id: ${id}`)
    return undefined
  }

  const userAccess = await tx
    .select({ userId: partyUserRoleConstraint.userId })
    .from(partyUserRoleConstraint)
    .where(
      and(
        eq(partyUserRoleConstraint.agencyId, id),
        eq(partyUserRoleConstraint.constraintType, "AGENCY"),
        eq(partyUserRoleConstraint.active, true),
      ),
    )

  return buildAgencyDoc(
    agencyItem,
    userAccess.map((u) => u.userId),
  )
}

/** Bulk fetch — batch queries for a chunk of IDs, returns Map<id, AgencyDoc> */
export const getAgencyBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<Map<string, AgencyDoc>> => {
  if (ids.length === 0) return new Map()

  const BATCH_SIZE = 2000

  // 1. Batch fetch all agency rows (only active agencies)
  const agencyRows: AgencyRow[] = []
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const batch = ids.slice(i, i + BATCH_SIZE) as string[]
    const batchRows = await tx
      .select({
        id: partyAgency.id,
        identifier: partyAgency.identifier,
        agency_type: partyAgency.agencyType,
        display_name: partyAgency.displayName,
        physical_address: partyAgency.physicalAddress,
        physical_city: partyAgency.city,
        physical_state: partyAgency.state,
        physical_country_code: partyAgency.countryCode,
        source_system_id: partyAgency.sourceSystemId,
      })
      .from(partyAgency)
      .where(and(inArray(partyAgency.id, batch), eq(partyAgency.active, true)))
    agencyRows.push(...batchRows)
  }

  if (agencyRows.length === 0) return new Map()

  // 2. Batch fetch all user access constraints for these agencies
  type UserAccessRow = { agencyId: string | null; userId: string }
  const userAccessRows: UserAccessRow[] = []
  const agencyIds = agencyRows.map((r) => r.id)
  for (let i = 0; i < agencyIds.length; i += BATCH_SIZE) {
    const batch = agencyIds.slice(i, i + BATCH_SIZE)
    const batchRows = await tx
      .select({
        agencyId: partyUserRoleConstraint.agencyId,
        userId: partyUserRoleConstraint.userId,
      })
      .from(partyUserRoleConstraint)
      .where(
        and(
          inArray(partyUserRoleConstraint.agencyId, batch),
          eq(partyUserRoleConstraint.constraintType, "AGENCY"),
          eq(partyUserRoleConstraint.active, true),
        ),
      )
    userAccessRows.push(...batchRows)
  }

  // Index user access by agency ID
  const userAccessByAgency = new Map<string, string[]>()
  for (const row of userAccessRows) {
    if (!row.agencyId) continue
    const users = userAccessByAgency.get(row.agencyId) ?? []
    users.push(row.userId)
    userAccessByAgency.set(row.agencyId, users)
  }

  // 3. Build docs
  const result = new Map<string, AgencyDoc>()
  for (const row of agencyRows) {
    const userAccess = userAccessByAgency.get(row.id) ?? []
    result.set(row.id, buildAgencyDoc(row, userAccess))
  }

  return result
}

export const saveAgencyToSearch = async (
  tx: DbTransaction,
  agencyId: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const doc = await getAgencyData(tx, agencyId)
  if (!doc || Object.keys(doc).length === 0) {
    console.error(`Failed to prepare document for id: ${agencyId}`)
    return
  }

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName,
      DataType: "String",
    },
  }
  const command = new SendMessageCommand({
    QueueUrl: queue_url,
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  await sqs_client.send(command)
}
